import { useState } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {object} LicenseStats
 * @property {number} total - Total licenses purchased
 * @property {number} assigned - Licenses assigned
 * @property {number} used - Licenses used
 * @property {number} remaining - Licenses remaining/available
 */

/**
 * @typedef {object} Organization
 * @property {number} id - Organization ID
 * @property {string} name - Organization name
 */

/**
 * @typedef {object} SchoolItem
 * @property {number} id - School ID
 * @property {string} name - School name
 * @property {string|null} image - School image
 * @property {string|null} image_url - School image URL
 */

/**
 * @typedef {object} OrganizationStats
 * @property {number} total_schools - Total number of schools
 * @property {number} total_teachers - Total number of teachers
 * @property {number} total_students - Total number of students
 * @property {LicenseStats} licenses - License statistics
 * @property {SchoolItem[]} schools - List of schools
 * @property {Organization} organization - Organization details
 */

/**
 * Custom hook to fetch organization dashboard stats
 */
export const useGetOrganizationStats = () => {
  const [stats, setStats] = useState(
    /** @type {OrganizationStats | null | undefined} */(undefined),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch Organization Dashboard Stats
   * @async
   * @returns {Promise<void>}
   */
  const getOrganizationStats = async () => {
    try {
      setLoading(true);
      setError("");

      const url = `${TENANT_DOMAIN}/organization/admin/dashboard`;
      const response = await axiosInstance.get(url);

      setStats(response?.data || null);
    } catch (err) {
      setStats(null);

      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    stats,
    setStats,
    loading,
    error,
    getOrganizationStats,
  };
};
